import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../../hooks/useAuth';

function friendlyAuthError(err: unknown): string {
  const message = err instanceof Error ? err.message : '';
  if (
    message.includes('auth/invalid-credential') ||
    message.includes('auth/wrong-password') ||
    message.includes('auth/user-not-found') ||
    message.includes('INVALID_LOGIN_CREDENTIALS')
  ) {
    return 'Invalid email or password.';
  }
  if (message.includes('auth/too-many-requests')) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  if (message.includes('auth/invalid-email')) {
    return 'Please enter a valid email address.';
  }
  return 'Sign-in failed. Please try again or contact your Faana admin.';
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: '#555' },
    '&:hover fieldset': { borderColor: '#888' },
  },
  '& .MuiInputLabel-root': { color: '#888' },
};

function EmailPasswordForm({ setError }: { setError: (msg: string | null) => void }) {
  const { loginWithPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await loginWithPassword(email, password);
    } catch (err: unknown) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Email"
        type="email"
        size="small"
        fullWidth
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        sx={fieldSx}
      />
      <TextField
        label="Password"
        type="password"
        size="small"
        fullWidth
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        sx={fieldSx}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        sx={{ py: 1.2, bgcolor: '#fff', color: '#1a1a1a', '&:hover': { bgcolor: '#ddd' } }}
      >
        {submitting ? <CircularProgress size={20} /> : 'Sign in'}
      </Button>
    </Box>
  );
}

export default function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async () => {
    setError(null);
    setSigningIn(true);
    try {
      await login();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      if (!message.includes('popup-closed-by-user')) {
        setError(message);
      }
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#1E1E1E' }}
    >
      <Box
        sx={{
          width: 400,
          p: 5,
          borderRadius: 3,
          bgcolor: '#2A2A2A',
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src="/assets/faana-logo.png"
          alt="Faana"
          sx={{ width: 56, height: 56, mb: 2 }}
        />
        <Typography variant="body2" sx={{ color: '#888', mb: 4 }}>
          Faana Portal
        </Typography>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleLogin}
          disabled={signingIn}
          sx={{
            color: '#fff',
            borderColor: '#555',
            py: 1.5,
            '&:hover': { borderColor: '#888', bgcolor: 'rgba(255,255,255,0.05)' },
          }}
        >
          {signingIn ? <CircularProgress size={20} /> : 'Sign in with Google'}
        </Button>

        <Divider sx={{ my: 3, color: '#666', '&::before, &::after': { borderColor: '#444' } }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            or sign in with email
          </Typography>
        </Divider>

        <EmailPasswordForm setError={setError} />

        {error && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
